import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@shared/constants';

/**
 * Error thrown by the API client when the server responds with a non-2xx
 * status. Carries the HTTP status, the server-defined error code (if any),
 * and a human-readable message.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  override message: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.message = message;
  }
}

/** Read a cookie value by name from document.cookie. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

/**
 * The single place the app talks to the server.
 *
 * - Prefixes every path with `/api/v1`.
 * - Sends `credentials: 'include'` so the HttpOnly session cookie is
 *   forwarded.
 * - Sets `Content-Type: application/json` for JSON bodies.
 * - Attaches the CSRF token header on state-changing methods
 *   (POST/PATCH/PUT/DELETE), reading the token from the non-HttpOnly
 *   `bc_csrf` cookie.
 * - On a non-2xx response, parses `{ error: { code, message } }` and throws
 *   an `ApiClientError`. A 401 throws with code `'unauthorized'`.
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = path.startsWith('/api/v1') ? path : `/api/v1${path}`;

  const method = (options?.method ?? 'GET').toUpperCase();
  const isJsonBody = options?.body !== undefined && options.body !== null;
  const isStateChanging = ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method);

  const headers = new Headers(options?.headers);

  if (isJsonBody) {
    headers.set('Content-Type', 'application/json');
  }

  if (isStateChanging) {
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      headers.set(CSRF_HEADER_NAME, csrfToken);
    }
  }

  const response = await fetch(url, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let code = 'error';
    let message = response.statusText;

    try {
      const body = await response.json();
      if (body && typeof body === 'object' && 'error' in body) {
        const err = (body as { error: { code?: string; message?: string } }).error;
        if (typeof err?.code === 'string') code = err.code;
        if (typeof err?.message === 'string') message = err.message;
      }
    } catch {
      // Body wasn't JSON — fall back to status text.
    }

    if (response.status === 401) {
      code = 'unauthorized';
    }

    throw new ApiClientError(response.status, code, message);
  }

  return response.json() as Promise<T>;
}

export function apiGet<T>(path: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'GET' });
}

export function apiPost<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
  return apiFetch<T>(path, {
    ...options,
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
  return apiFetch<T>(path, {
    ...options,
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(path: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'DELETE' });
}
